import React, { useCallback } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { tokens } from '@hotjar/hotsauce';
import { getMinutesAndSeconds } from '@hotjar/webapp/lib/getMinutesAndSeconds';
import { stripProtocol } from '@hotjar/webapp/utils/urls';

import { useRealTimeStore } from '../../store/realTimeStore';
import Zzz from './icons/zzz.svg';

const msToRoundedMinutes = (ms: number) => Math.round(ms / 60000);

const msToRoundedSeconds = (ms: number) => Math.round(ms / 1000);

const getInactiveDurationTimeLabelByMs = (ms: number, t: TFunction) => {
  return ms < 60000
    ? `${msToRoundedSeconds(ms)} ${t('common:player.timeline.seconds-abbreviated')}`
    : `${msToRoundedMinutes(ms)} ${t('common:player.timeline.minutes-abbreviated')}`;
};

const WrapperDiv = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 176px;
  max-width: 328px;
`;

const UrlDiv = styled.div`
  padding: ${tokens.spacer.x1} ${tokens.spacer.x2};
  ${tokens.text('caption')}
  font-weight: 500;
  line-height: 20px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  // We want the ellipsis to be on the left side
  direction: rtl;
`;

const LtrSpan = styled.span`
  // We have to use this hack to avoid having the character reordered due to rtl direction.
  // Without the hack: "text.com/" would be displayed "/text.com"
  unicode-bidi: embed;
  direction: ltr;
`;

const BottomDiv = styled.div`
  padding: 0 ${tokens.spacer.x2} ${tokens.spacer.x1} ${tokens.spacer.x2};
  ${tokens.text('caption')}
  color: ${tokens.color.text_light_inverse};
`;

const FooterDiv = styled.div`
  width: 100%;
  padding: ${tokens.spacer.x1} ${tokens.spacer.x2};
  ${tokens.text('caption')}
  color: ${tokens.color.white};
  background-color: ${tokens.color.neutral_600};
  border-bottom-left-radius: ${tokens.spacer.x0};
  border-bottom-right-radius: ${tokens.spacer.x0};
`;

interface Props {
  isDragging: boolean;
  id?: string;
}

const TabInfo = ({
  currentTabNumber,
  totalTab,
}: {
  currentTabNumber: number;
  totalTab: number;
}): JSX.Element => {
  const { t } = useTranslation();

  return (
    <>
      {' '}
      •{' '}
      <span>
        {t('common:player.timeline.tab-of', {
          currentTabNumber,
          totalTab,
        })}
      </span>
    </>
  );
};

export const SectionTooltipContent = ({ isDragging, id }: Props): JSX.Element => {
  const { t } = useTranslation();
  const usefulSectionInfo = useRealTimeStore(
    useCallback(
      (state) =>
        isDragging
          ? state.sectionInfoList[state.draggedSectionIndex]
          : state.sectionInfoList[state.hoveredSectionIndex],
      [isDragging],
    ),
  );
  const usefulTime = useRealTimeStore(
    useCallback((state) => (isDragging ? state.draggedTime : state.hoveredTime), [isDragging]),
  );
  const totalPage = useRealTimeStore(
    // FIXME: Unsafe arithmetic operation on optional chaining. It can result in NaN.
    // eslint-disable-next-line no-unsafe-optional-chaining
    (state) => state.sectionInfoList[state.sectionInfoList.length - 1]?.pageIndex + 1,
  );
  const { tabCount } = useRealTimeStore.getState();

  return (
    <WrapperDiv id={id}>
      {usefulSectionInfo && (
        <>
          <UrlDiv>
            <LtrSpan>{stripProtocol(usefulSectionInfo.url)}</LtrSpan>
          </UrlDiv>
          <BottomDiv>
            <span>{getMinutesAndSeconds(usefulTime)}</span> •{' '}
            <span>
              {t('common:player.timeline.page-of', {
                currentPageNumber: usefulSectionInfo.pageIndex + 1,
                totalPage,
              })}
            </span>
            <TabInfo currentTabNumber={usefulSectionInfo.tabIndex + 1} totalTab={tabCount} />
          </BottomDiv>
          {!usefulSectionInfo.isActive && (
            <FooterDiv>
              <img src={Zzz} alt="inactive-icon" />
              &nbsp;&nbsp;{t('common:player.timeline.user-was-inactive')}
              &nbsp;
              {getInactiveDurationTimeLabelByMs(usefulSectionInfo.durationInMs, t)}
            </FooterDiv>
          )}
        </>
      )}
    </WrapperDiv>
  );
};
